import { createContext, useContext } from 'react';
import { QM_RECOVERY } from './constants';
import type { RecordingStatusDTO, RecordingSummary } from './types';
import type { CalendarShellAccess } from './useCalendarShellAccess';
import type { RemoteRecording } from './useCrossTabRecording';
import type { MicLevelStore } from './useMicLevel';
import type { QuickMeetingMicValue } from './useQuickMeetingMic';
import type { LocalRecording, StartInput } from './useQuickMeetingRecorder';
import type { useRecordingRowActions } from './useRecordingRowActions';

export interface QuickMeetingContextValue {
  access: CalendarShellAccess;
  /** `GET /recordings/status`; undefined while loading or after a first-load failure. */
  status: RecordingStatusDTO | undefined;
  /** When `status` was last fetched (`Date.now()` ms); 0 before the first answer. */
  statusFetchedAt: number;
  /** `clarix-quick-meeting:{userId}:{slug}`, or null while signed out. */
  scope: string | null;
  local: LocalRecording;
  remote: RemoteRecording | null;
  /** This tab's live mic level; an external store so its ticks re-render only the meter. */
  micLevel: MicLevelStore;
  /** The mic picker's options and choice, and whether the live take lost its mic. */
  mic: QuickMeetingMicValue;
  /** Start is allowed: `recordings.create` and the server says available. */
  canStart: boolean;
  noticeVersion: string;
  start: (input: StartInput) => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  retrySave: () => Promise<boolean>;
  /** Discard this tab's unsaved take (after a confirm); false when the server kept it. */
  discardOwn: () => Promise<boolean>;
  /** Save this tab's take, as kept on this device, to a file. */
  downloadOwn: () => void;
  stopRemote: () => void;
  clearError: () => void;
  rows: ReturnType<typeof useRecordingRowActions>;
  /** Same tab inside Calendar or on an app-less page; a new tab from another app. */
  openCalendarPath: (path: string) => void;
  /** The TopBar cluster registers itself; with none mounted, a floating indicator shows. */
  attachTopBar: () => () => void;
}

export const QuickMeetingContext = createContext<QuickMeetingContextValue | null>(null);

/** Null outside WorkspaceShell (the chrome renders nothing there). */
export function useQuickMeeting(): QuickMeetingContextValue | null {
  return useContext(QuickMeetingContext);
}

/** What an orphan check may trust: the status read's age and whether this tab is on screen. */
export interface OrphanClock {
  now: number;
  /** When the status was fetched. */
  fetchedAt: number;
  /** A hidden tab's polls are throttled, so it never judges. */
  visible: boolean;
}

/**
 * The server's `recording` row when nothing drives it any more: no tab here owns it, and the server's
 * `heartbeat_age_seconds` (plus the time since that read) is over 3 minutes. Only a fresh read, on a
 * visible tab, counts; without the age (an older API) nothing is flagged. A just-finished take is skipped.
 */
export function orphanedRecording(
  status: RecordingStatusDTO | undefined,
  local: LocalRecording,
  remote: RemoteRecording | null,
  clock: OrphanClock,
): RecordingSummary | null {
  const active = status?.active;
  if (!active || remote || active.id === local.endedId || !clock.visible) return null;
  if (local.phase !== 'idle' && local.phase !== 'denied') return null;
  return isOrphanAge(active.heartbeat_age_seconds, clock.now - clock.fetchedAt) ? active : null;
}

/** A heartbeat this old, read `sinceReadMs` ago, means no tab anywhere drives the take. */
export function isOrphanAge(ageSeconds: number | null | undefined, sinceReadMs: number): boolean {
  if (ageSeconds == null || sinceReadMs > QM_RECOVERY.STATUS_FRESH_MS) return false;
  // A clock that ticks slower than the poll can trail the read; that read is simply brand new.
  return ageSeconds * 1000 + Math.max(0, sinceReadMs) > QM_RECOVERY.ORPHAN_AFTER_MS;
}
